import { Box, Container, Grid, Typography, Divider, IconButton } from '@mui/material';
import { LogIn, MapPin, Phone, Mail } from 'lucide-react';
import { FaFacebook, FaTwitter, FaInstagram, FaYoutube } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

export function Footer({ data }) {
  const navigate = useNavigate();
  const primaryColor = data.primary_color || '#1e3a8a';
  const schoolName = data.school_name || 'Hera Public School';
  const erpEnabled = data.erp_enabled !== false;
  const contact = data.contact || {};
  const social = data.social_media || {};

  return (
    <Box component="footer" sx={{ backgroundColor: '#0f172a', color: '#94a3b8', pt: 8, pb: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={5} sx={{ mb: 6 }}>
          {/* Col 1: School Branding */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="h5" fontWeight={900} color="#ffffff" sx={{ mb: 2, letterSpacing: '-0.02em' }}>
              {schoolName}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 3 }}>
              {data.about_content
                ? `${data.about_content.substring(0, 140)}...`
                : 'Committed to providing a nurturing learning environment where students develop strong academic foundations and character.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {social.facebook && (
                <IconButton component="a" href={social.facebook} target="_blank" sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
                  <FaFacebook size={18} />
                </IconButton>
              )}
              {social.twitter && (
                <IconButton component="a" href={social.twitter} target="_blank" sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
                  <FaTwitter size={18} />
                </IconButton>
              )}
              {social.instagram && (
                <IconButton component="a" href={social.instagram} target="_blank" sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
                  <FaInstagram size={18} />
                </IconButton>
              )}
              {social.youtube && (
                <IconButton component="a" href={social.youtube} target="_blank" sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
                  <FaYoutube size={18} />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Col 2: Quick Links */}
          <Grid size={{ xs: 6, md: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} color="#ffffff" sx={{ mb: 2.5 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {['Home', 'About', 'Academics', 'Facilities', 'Events', 'Gallery', 'Contact'].map((link) => (
                <Typography
                  key={link}
                  component="a"
                  href={`#${link.toLowerCase()}`}
                  variant="body2"
                  sx={{
                    color: '#94a3b8',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                    '&:hover': { color: '#ffffff' },
                  }}
                >
                  {link}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Col 3: Admissions & Academics */}
          <Grid size={{ xs: 6, md: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} color="#ffffff" sx={{ mb: 2.5 }}>
              Academics
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {['Primary Education', 'Middle School', 'Secondary School', 'Senior Secondary', 'STEM Program'].map((item) => (
                <Typography key={item} variant="body2" sx={{ color: '#94a3b8' }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Col 4: Contact & ERP Link */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} color="#ffffff" sx={{ mb: 2.5 }}>
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <MapPin size={18} color="#ffffff" style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography variant="body2">{contact.address || 'Sector 50, Noida, UP'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Phone size={16} color="#ffffff" style={{ flexShrink: 0 }} />
                <Typography variant="body2">{contact.phone || '+91 98765 43210'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Mail size={16} color="#ffffff" style={{ flexShrink: 0 }} />
                <Typography variant="body2">{contact.email || 'info@herapublicschool.edu'}</Typography>
              </Box>
            </Box>

            {/* ERP Link */}
            {erpEnabled && (
              <Typography
                component="a"
                onClick={() => navigate('/ERP')}
                variant="body2"
                sx={{
                  color: '#94a3b8',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#ffffff' },
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                }}
              >
                <LogIn size={16} />
                School ERP Login
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: '#1e293b', mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="#64748b">
            © {new Date().getFullYear()} {schoolName}. All Rights Reserved.
          </Typography>
          <Typography variant="caption" color="#64748b">
            Powered by Harivansh Digital
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
